import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ReCAPTCHA from "react-google-recaptcha";

import NavBar from "../components/NavBar";
import BackgroundImage from "../components/BackgroundImage";
import TransLink from "../components/TransLink";
import {
  trackFormSubmission,
  sendFormToTelegram,
  sendLeadToSupabase,
} from "../utils/eventTracker";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrochip,
  faMobileScreenButton,
  faServer,
  faGlobeAmericas,
} from "@fortawesome/free-solid-svg-icons";
import Footer from "../components/Footer";

export default function Quote() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    description: "",
    dateTime: "",
  });

  const nameRef = useRef(null);
  const contactRef = useRef(null);
  const descriptionRef = useRef(null);
  const dateTimeRef = useRef(null);

  const [error, setError] = useState(null);

  const validate = () => {
    if (!formData.name.trim()) {
      setError({ field: "name", message: t("formErrorName") });
      nameRef.current?.focus();
      return false;
    }
    if (!formData.contact.trim()) {
      setError({ field: "contact", message: t("formErrorContact") });
      contactRef.current?.focus();
      return false;
    }
    if (!formData.description.trim()) {
      setError({ field: "description", message: t("formErrorDescription") });
      descriptionRef.current?.focus();
      return false;
    }
    /*if (!formData.dateTime.trim()) {
      setError({ field: "dateTime", message: t("formErrorDateTime") });
      dateTimeRef.current?.focus();
      return false;
    }*/

    setError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🕵️‍♂️ Honeypot: if filled, likely a bot
    if (formData.botField && formData.botField.trim() !== "") {
      console.warn("Bot detected. Submission ignored.");
      return;
    }

    // 🧠 Validate user-entered fields
    if (!validate()) return;

    // 🧷 Check if user completed CAPTCHA
    if (!captchaToken) {
      setError({
        field: "captcha",
        message: t("formErrorCaptcha") || "Please complete the captcha.",
      });
      return;
    }

    try {
      // 📦 Build payload with CAPTCHA token
      const payload = {
        ...formData,
        captchaToken,
      };

      // 📊 Tracking + send
      trackFormSubmission("quotePage");

      const success = await sendFormToTelegram(payload);

      if (success) {
        await sendLeadToSupabase(payload);
        navigate("/success");
      }
    } catch (err) {
      console.error("Submission failed:", err);
      setError({
        field: "form",
        message: "Something went wrong. Try again later.",
      });
    } finally {
      try {
        recaptchaRef.current?.reset();
      } catch (err) {
        console.warn("⚠️ reCAPTCHA reset failed:", err);
      }
      setCaptchaToken("");
    }
  };

  return (
    <div className="page-wrapper">
      <BackgroundImage />
      <NavBar />
      <div className="transparency-wrapper">
        <div className="quote-wrapper">
          <div className="quote-title-wrapper no-margin-bottom breathe">
            <div className="section-title-centered neon-glow">
              {t("quoteTitle")}
            </div>
            <div className="section-subtitle-centered cyan">
              {t("quoteSubtitle")}
            </div>
          </div>

          <div className="quote-form-wrapper">
            {/*<img
              src="/images/quote_picture.webp"
              alt={t("quoteImageAlt")}
              className="quote-floating-image"
            />*/}

            <div className="quote-text-intro-wrapper">
              <TransLink
                t="quoteIntro"
                textStyle="section-text-secondary-quote"
                linkStyle="project-link"
              />

              <p className="section-text-secondary-quote">
                <FontAwesomeIcon icon={faMicrochip} className="quote-icon" />{" "}
                {t("quoteLine1")}
              </p>
              <p className="section-text-secondary-quote">
                <FontAwesomeIcon
                  icon={faMobileScreenButton}
                  className="quote-icon"
                />{" "}
                {t("quoteLine2")}
              </p>
              <p className="section-text-secondary-quote">
                <FontAwesomeIcon icon={faServer} className="quote-icon" />{" "}
                {t("quoteLine3")}
              </p>
              <p className="section-text-secondary-quote">
                <FontAwesomeIcon
                  icon={faGlobeAmericas}
                  className="quote-icon"
                />
                <TransLink
                  t="quoteLine4"
                  textStyle="section-text-secondary-quote"
                  linkStyle="project-link"
                />
              </p>

              <TransLink
                t="quoteFinalMessage"
                textStyle="section-text-secondary-quote"
                linkStyle="project-link"
              />
            </div>

            <div className="quote-text-form-wrapper">
              <form
                className="quote-form"
                id="quote-form"
                onSubmit={handleSubmit}
              >
                {/* Honeypot field (invisible to humans) */}
                <input
                  type="text"
                  name="botField"
                  style={{ display: "none" }}
                  value={formData.botField || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, botField: e.target.value })
                  }
                />

                {/* Name */}
                <div className="quote-field-wrapper">
                  <label className="quote-label-text">
                    {t("formNameLabel")}
                  </label>
                  <input
                    ref={nameRef}
                    type="text"
                    className={`form-field${
                      error?.field === "name" ? " form-error" : ""
                    }`}
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      setError(null);
                    }}
                  />
                  {error?.field === "name" && (
                    <p className="form-error-text">{error.message}</p>
                  )}
                </div>

                {/* Contact */}
                <div className="quote-field-wrapper">
                  <label className="quote-label-text">
                    {t("formContactLabel")}
                  </label>
                  <input
                    ref={contactRef}
                    type="text"
                    className={`form-field${
                      error?.field === "contact" ? " form-error" : ""
                    }`}
                    value={formData.contact}
                    onChange={(e) => {
                      setFormData({ ...formData, contact: e.target.value });
                      setError(null);
                    }}
                  />
                  {error?.field === "contact" && (
                    <p className="form-error-text">{error.message}</p>
                  )}
                </div>

                {/* Description */}
                <div className="quote-field-wrapper">
                  <label className="quote-label-text">
                    {t("formDescriptionLabel")}
                  </label>
                  <textarea
                    ref={descriptionRef}
                    rows={2}
                    className={`form-field${
                      error?.field === "description" ? " form-error" : ""
                    }`}
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      setError(null);
                    }}
                  />
                  {error?.field === "description" && (
                    <p className="form-error-text">{error.message}</p>
                  )}
                </div>

                {/* Preferred time */}
                <div className="quote-field-wrapper">
                  <label className="quote-label-text">
                    {t("formDateTimeLabel")}
                  </label>
                  <input
                    ref={dateTimeRef}
                    type="text"
                    className={`form-field${
                      error?.field === "dateTime" ? " form-error" : ""
                    }`}
                    value={formData.dateTime}
                    onChange={(e) => {
                      setFormData({ ...formData, dateTime: e.target.value });
                      setError(null);
                    }}
                  />
                  {error?.field === "dateTime" && (
                    <p className="form-error-text">{error.message}</p>
                  )}
                </div>

                {/* reCAPTCHA v2 checkbox */}
                <div className="captcha-wrapper">
                  <ReCAPTCHA
                    sitekey="6LegCA4sAAAAAC0Wy9Up9tKxH-569DVT0g4wuU9-"
                    onChange={(token) => setCaptchaToken(token)}
                    ref={recaptchaRef}
                  />
                  {error?.field === "captcha" && (
                    <p className="form-error-text">{error.message}</p>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="cta-button-secondary-wrapper-bottom">
            <button type="submit" form="quote-form" className="cta-button">
              {t("formSubmitCTA")}
            </button>
            <div className="bottom-space"></div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
